class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      if (!event.isTrusted) return;
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (!event.isTrusted) return;
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.lastOpenedAt = Date.now();
      history.pushState({ cartDrawerOpen: true }, '', '#cart-drawer'); 
      setupCartCopyCode(); // Initialize copy code functionality when drawer opens
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (location.hash === '#cart-drawer') {
      history.back();
    }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      setupCartCopyCode(); // Initialize copy code functionality
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

window.addEventListener('popstate', (event) => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer?.lastOpenedAt && Date.now() - cartDrawer.lastOpenedAt < 700) {
    return;
  }
  if (cartDrawer && cartDrawer.classList.contains('active')) {
    cartDrawer.close();
  }
})

customElements.define('cart-drawer-items', CartDrawerItems);

// Copy code functionality for cart drawer
function setupCartCopyCode() {
  const codeBox = document.getElementById('cart-coupon-code-box');
  if (!codeBox) return;
  
  const codeTextElement = document.getElementById('cart-coupon-code-text');
  const copyIcon = document.getElementById('cart-copy-icon');
  const checkIcon = document.getElementById('cart-check-icon');
  
  if (!codeTextElement || !copyIcon || !checkIcon) return;
  
  const originalText = codeTextElement.textContent;
  const codeToCopy = codeBox.getAttribute('data-code-to-copy');

  codeBox.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(codeToCopy);
      
      // Visual feedback - change text and swap icons
      codeTextElement.textContent = 'Copied';
      copyIcon.style.display = 'none';
      checkIcon.style.display = 'block';
      codeBox.style.backgroundColor = 'rgba(var(--color-primary-rgb, 199, 116, 24), 0.15)';
      
      // Reset after 2 seconds
      setTimeout(() => {
        codeTextElement.textContent = originalText;
        copyIcon.style.display = 'block';
        checkIcon.style.display = 'none';
        codeBox.style.backgroundColor = '';
      }, 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  });
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupCartCopyCode);
} else {
  setupCartCopyCode();
}

// Re-initialize when cart drawer content updates
document.addEventListener('cart:updated', setupCartCopyCode);